import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiErrorDetail, formatDateIT } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { emitServicesChanged, onServicesChanged } from "@/lib/servicesSync";
import MinutesUsageChart from "@/components/MinutesUsageChart";
import {
  ArrowLeft, Phone, Envelope, MapPin, Key, EyeSlash, Eye,
  Plus, Trash, Clock, Warning, ShieldCheck, ShieldStar, CalendarBlank,
  PencilSimple, FloppyDisk, Info, Wrench, HouseSimple,
} from "@phosphor-icons/react";

const eur = (n) => n == null ? "—" : n.toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });

function formatMinutes(m) {
  const n = parseInt(m || 0, 10);
  const h = Math.floor(n / 60);
  const min = n % 60;
  if (h === 0) return `${min}min`;
  if (min === 0) return `${h}h`;
  return `${h}h ${min}min`;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function AssistanceCustomerPage() {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [services, setServices] = useState([]);
  const [logs, setLogs] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [plan, setPlan] = useState(null);
  const [remote, setRemote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [credsEdit, setCredsEdit] = useState(false);
  const [credsValue, setCredsValue] = useState("");
  const [showCreds, setShowCreds] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [intPage, setIntPage] = useState(1);
  const PAGE_SIZE = 10;

  useDocumentTitle(customer ? `Assistenza · ${customer.surname} ${customer.name}` : "Assistenza cliente");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, l, p, r, iv] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/customers/${id}/services`),
        api.get(`/customers/${id}/assistance-logs`),
        api.get(`/customers/${id}/managed-plan`),
        api.get(`/customers/${id}/remote-minutes`),
        api.get(`/customers/${id}/interventions`),
      ]);
      setCustomer(c.data);
      setServices(s.data);
      setLogs(l.data);
      setPlan(p.data);
      setRemote(r.data);
      setInterventions(iv.data);
      setCredsValue(c.data.access_credentials || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const off = onServicesChanged(id, () => { load(); });
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { off(); document.removeEventListener("visibilitychange", onVis); };
  }, [id, load]);

  const removeLog = async (log) => {
    if (!window.confirm(`Eliminare questa assistenza del ${formatDateIT(log.date)}?`)) return;
    try {
      await api.delete(`/assistance-logs/${log.id}`);
      toast.success("Assistenza eliminata");
      emitServicesChanged(id);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveCredentials = async () => {
    setSavingCreds(true);
    try {
      const { data } = await api.patch(`/customers/${id}`, { access_credentials: credsValue.trim() || null });
      setCustomer(data);
      toast.success("Dati di accesso salvati");
      setCredsEdit(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingCreds(false);
    }
  };

  if (loading) return <div className="p-10 text-muted-foreground">Caricamento…</div>;
  if (!customer) return <div className="p-10 text-muted-foreground">Cliente non trovato</div>;

  const otherServices = services.filter((s) => s.category !== "pacchetto_minuti" && s.category !== "pacchetto_assistenza");

  const hasPlan = !!plan;
  const planTotal = plan?.monthly_minutes_total ?? 0;
  const planUsed = plan?.monthly_minutes_used ?? 0;
  const planRem = plan?.monthly_minutes_remaining ?? 0;
  const planPct = planTotal > 0 ? Math.min(100, (planUsed / planTotal) * 100) : 0;

  const remoteAvail = remote?.available ?? 0;
  const totalCoverage = planRem + remoteAvail;

  // Paginazione storici
  const logsTotalPages = Math.max(1, Math.ceil(logs.length / PAGE_SIZE));
  const logsCurPage = Math.min(logsPage, logsTotalPages);
  const logsStart = (logsCurPage - 1) * PAGE_SIZE;
  const logsPageItems = logs.slice(logsStart, logsStart + PAGE_SIZE);

  const intTotalPages = Math.max(1, Math.ceil(interventions.length / PAGE_SIZE));
  const intCurPage = Math.min(intPage, intTotalPages);
  const intStart = (intCurPage - 1) * PAGE_SIZE;
  const intPageItems = interventions.slice(intStart, intStart + PAGE_SIZE);

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="assistance-customer-page">
      <Link to="/assistenze" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Elenco clienti
      </Link>

      <header className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">// ASSISTENZA CLIENTE</div>
          <h1 className="text-3xl font-display font-bold tracking-tight">{customer.surname} {customer.name}</h1>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><Phone size={13} /> <span className="font-mono">{customer.phone}</span></span>
            {customer.email && <span className="inline-flex items-center gap-1.5"><Envelope size={13} /> <a href={`mailto:${customer.email}`} className="text-primary hover:underline">{customer.email}</a></span>}
            {customer.address && <span className="inline-flex items-center gap-1.5"><MapPin size={13} /> {customer.address}</span>}
          </div>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          disabled={totalCoverage <= 0}
          data-testid="new-assistance-btn"
          className="rounded-full gap-2"
          title={totalCoverage <= 0 ? "Nessuna copertura minuti disponibile per questo cliente" : ""}
        >
          <Plus size={16} weight="bold" /> Registra assistenza
        </Button>
      </header>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT: services + credentials */}
        <div className="lg:col-span-1 space-y-4">
          <Card className="p-5 border-border/70 rounded-lg" data-testid="services-readonly-card">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={18} weight="duotone" className="text-primary" />
              <h2 className="font-display font-bold tracking-tight">Servizi attivi</h2>
            </div>
            {otherServices.length === 0 ? (
              <div className="text-xs text-muted-foreground/70 font-mono">— nessuno —</div>
            ) : (
              <div className="space-y-2">
                {otherServices.map((s) => (
                  <div key={s.id} className="text-sm border border-border/60 rounded px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium truncate">{s.name}</div>
                      {s.category && <Badge className="bg-secondary text-foreground border-border hover:bg-secondary font-mono text-[9px] uppercase shrink-0">{s.category}</Badge>}
                    </div>
                    <div className="text-[11px] text-muted-foreground font-mono mt-0.5 flex gap-3">
                      <span>Scad: {formatDateIT(s.expires_at)}</span>
                      {s.price != null && <span>{eur(s.price)}/anno</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[10px] text-muted-foreground mt-3 font-mono">
              Modifica dei servizi via <Link to={`/clienti/${customer.id}`} className="text-primary underline">Scheda cliente</Link>.
            </div>
          </Card>

          <Card className={`border-border/70 rounded-lg ${!showCreds && !credsEdit ? "p-4" : "p-5"}`} data-testid="access-credentials-card">
            <div className={`flex items-center justify-between ${(!showCreds && !credsEdit) ? "" : "mb-3"}`}>
              <button
                type="button"
                onClick={() => customer.access_credentials && !credsEdit && setShowCreds((v) => !v)}
                className="flex items-center gap-2 text-left"
                data-testid="toggle-credentials-visibility"
              >
                <Key size={18} weight="duotone" className="text-primary" />
                <h2 className="font-display font-bold tracking-tight">Dati di accesso</h2>
                {customer.access_credentials && !credsEdit && (
                  <span className="text-xs text-muted-foreground font-mono ml-2">
                    {showCreds ? <><EyeSlash size={13} className="inline" /> nascondi</> : <><Eye size={13} className="inline" /> mostra</>}
                  </span>
                )}
              </button>
              {!credsEdit && (
                <Button variant="ghost" size="sm" onClick={() => setCredsEdit(true)} className="gap-1 rounded-full" data-testid="edit-credentials-btn">
                  <PencilSimple size={13} /> Modifica
                </Button>
              )}
            </div>

            {!credsEdit && !showCreds && !customer.access_credentials && (
              <div className="text-xs text-muted-foreground/70 font-mono mt-2">— Nessun dato salvato · clicca "Modifica" —</div>
            )}

            {!credsEdit && showCreds && customer.access_credentials && (
              <pre className="text-xs font-mono whitespace-pre-wrap bg-secondary/60 rounded p-3 border border-border/60 mt-2 max-h-96 overflow-y-auto" data-testid="credentials-view">
                {customer.access_credentials}
              </pre>
            )}

            {credsEdit && (
              <div className="space-y-2">
                <Textarea
                  rows={8}
                  value={credsValue}
                  onChange={(e) => setCredsValue(e.target.value)}
                  placeholder={"Esempio:\nRouter admin\nUser: admin\nPass: xyz123"}
                  className="font-mono text-xs"
                  data-testid="credentials-textarea"
                />
                <div className="flex gap-1 justify-end">
                  <Button variant="ghost" size="sm" onClick={() => { setCredsEdit(false); setCredsValue(customer.access_credentials || ""); }}>Annulla</Button>
                  <Button size="sm" onClick={saveCredentials} disabled={savingCreds} className="rounded-full gap-1" data-testid="save-credentials-btn">
                    <FloppyDisk size={13} /> {savingCreds ? "Salvo…" : "Salva"}
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Storico assistenze — nella colonna sinistra sotto i dati di accesso */}
          <Card className="border-border/70 rounded-lg overflow-hidden" data-testid="assistance-history-card">
            <div className="px-4 py-3 border-b border-border/60 bg-secondary/40 flex items-center gap-2">
              <Clock size={18} weight="duotone" className="text-primary" />
              <h2 className="font-display font-bold tracking-tight">Storico assistenze <span className="font-mono text-xs text-muted-foreground ml-1">({logs.length})</span></h2>
            </div>
            {logs.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground/70 font-mono">— Nessuna assistenza registrata —</div>
            ) : (
              <TooltipProvider delayDuration={100}>
                <div className="divide-y divide-border/50">
                  {logsPageItems.map((log) => (
                    <div key={log.id} className="p-4 flex items-start gap-3" data-testid={`log-row-${log.id}`}>
                      <div className="h-8 w-8 shrink-0 rounded-full bg-primary/10 text-primary grid place-items-center font-mono text-[10px]">
                        {log.date?.slice(8, 10)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-mono text-xs text-muted-foreground">{formatDateIT(log.date)}</div>
                          {log.minutes_used > 0 && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge className="bg-primary/15 text-primary border border-primary/30 hover:bg-primary/15 font-mono text-[9px] cursor-help" data-testid={`log-minutes-badge-${log.id}`}>
                                  -{formatMinutes(log.minutes_used)}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs font-mono text-[10px]">
                                <div className="font-semibold mb-1">Ripartizione minuti</div>
                                <div>Piano gestito: <span className="font-bold">{log.minutes_from_plan || 0}min</span></div>
                                <div>Saldo remoto: <span className="font-bold">{log.minutes_from_remote || 0}min</span></div>
                                {log.minutes_uncovered > 0 && (
                                  <div className="text-rose-400 font-semibold">Non coperti: {log.minutes_uncovered}min</div>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {log.minutes_uncovered > 0 && (
                            <Badge className="bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-500/40 hover:bg-rose-500/15 font-mono text-[9px] gap-1" data-testid={`log-uncovered-${log.id}`}>
                              <Warning size={10} weight="fill" /> Scoperti: {log.minutes_uncovered}min
                            </Badge>
                          )}
                          {log.service_name && !log.minutes_uncovered && (
                            <Badge className="bg-secondary text-foreground border-border hover:bg-secondary font-mono text-[9px] uppercase">
                              {log.service_name}
                            </Badge>
                          )}
                          {log.price != null && log.price > 0 && (
                            <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/15 font-mono text-[9px]">
                              {eur(log.price)}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm mt-1 whitespace-pre-wrap break-words">{log.note}</div>
                        {log.by_user && (
                          <div className="text-[10px] text-muted-foreground/70 font-mono mt-1">registrato da {log.by_user}</div>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeLog(log)} className="text-destructive hover:text-destructive rounded-full" data-testid={`delete-log-${log.id}`}>
                        <Trash size={13} />
                      </Button>
                    </div>
                  ))}
                </div>
                {logsTotalPages > 1 && (
                  <div className="px-4 py-2 border-t border-border/60 bg-secondary/20 flex items-center justify-between text-xs font-mono">
                    <span className="text-muted-foreground">Pagina {logsCurPage}/{logsTotalPages} · {logsStart + 1}-{Math.min(logsStart + PAGE_SIZE, logs.length)} di {logs.length}</span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" disabled={logsCurPage === 1} onClick={() => setLogsPage((p) => Math.max(1, p - 1))} data-testid="logs-prev-page" className="h-7 px-2 rounded-full">‹ Prec</Button>
                      <Button size="sm" variant="outline" disabled={logsCurPage === logsTotalPages} onClick={() => setLogsPage((p) => Math.min(logsTotalPages, p + 1))} data-testid="logs-next-page" className="h-7 px-2 rounded-full">Succ ›</Button>
                    </div>
                  </div>
                )}
              </TooltipProvider>
            )}
          </Card>
        </div>

        {/* RIGHT: coverage boxes + history */}
        <div className="lg:col-span-2 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {/* Managed Plan Box */}
            <Card className="p-4 border-border/70 rounded-lg" data-testid="coverage-plan-card">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <ShieldStar size={18} weight="duotone" className="text-primary" />
                  <h3 className="font-display font-bold text-sm tracking-tight">Piano Gestito</h3>
                </div>
                {hasPlan && <Badge className="bg-primary/10 text-primary border-primary/30 hover:bg-primary/10 font-mono text-[9px] uppercase">{plan.plan_label}</Badge>}
              </div>
              {!hasPlan ? (
                <div className="text-xs text-muted-foreground italic mt-3">
                  Nessun piano attivo. Attivalo dalla <Link to={`/clienti/${customer.id}`} className="text-primary underline">scheda cliente</Link>.
                </div>
              ) : (
                <>
                  <div className="mt-2">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-display font-bold" data-testid="coverage-plan-remaining">{planRem}</span>
                      <span className="text-sm text-muted-foreground">/ {planTotal} min mensili</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-secondary rounded overflow-hidden">
                      <div className={`h-full ${planRem === 0 ? "bg-rose-500" : planRem < planTotal * 0.2 ? "bg-amber-500" : "bg-emerald-500"}`}
                           style={{ width: `${planPct}%` }} />
                    </div>
                    <div className="mt-2 text-[10px] font-mono text-muted-foreground flex items-center gap-1">
                      <CalendarBlank size={11} /> Reset il 1° del mese · {plan.pc_count} PC × {plan.minutes_per_pc}min
                    </div>
                  </div>
                </>
              )}
            </Card>

            {/* Remote Balance Box */}
            <Card className="p-4 border-border/70 rounded-lg" data-testid="coverage-remote-card">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Clock size={18} weight="duotone" className="text-primary" />
                  <h3 className="font-display font-bold text-sm tracking-tight">Saldo Remoto</h3>
                </div>
                <Badge className="bg-secondary text-foreground border-border hover:bg-secondary font-mono text-[9px] uppercase">Prepagati</Badge>
              </div>
              <div className="mt-2">
                <div className="flex items-baseline gap-1">
                  <span className={`text-3xl font-display font-bold ${remoteAvail === 0 ? "text-muted-foreground" : ""}`} data-testid="coverage-remote-available">
                    {remoteAvail}
                  </span>
                  <span className="text-sm text-muted-foreground">min disponibili</span>
                </div>
                {remote && remote.total_purchased > 0 && (
                  <div className="mt-2 h-1.5 bg-secondary rounded overflow-hidden">
                    <div className={`h-full bg-primary`} style={{ width: `${Math.min(100, (remote.total_used / remote.total_purchased) * 100)}%` }} />
                  </div>
                )}
                <div className="mt-2 text-[10px] font-mono text-muted-foreground">
                  Non scadono · totale usato: {remote?.total_used ?? 0}min · totale acquistato: {remote?.total_purchased ?? 0}min
                </div>
                <div className="mt-2 text-[10px] font-mono text-muted-foreground">
                  Vendi pacchetti REMOTE dalla <Link to={`/clienti/${customer.id}`} className="text-primary underline">scheda cliente</Link>.
                </div>
              </div>
            </Card>
          </div>

          {/* Coverage summary line */}
          <div className={`rounded-md px-4 py-2 text-xs font-mono flex items-center justify-between ${totalCoverage <= 0 ? "bg-rose-500/10 border border-rose-500/40 text-rose-700 dark:text-rose-400" : "bg-secondary/50 border border-border/50 text-muted-foreground"}`} data-testid="coverage-summary">
            <span className="flex items-center gap-1.5">
              <Info size={13} weight="duotone" />
              Copertura totale disponibile
            </span>
            <span className="font-semibold">{totalCoverage} min ({planRem} piano + {remoteAvail} remoto)</span>
          </div>

          {/* Minutes usage chart */}
          <MinutesUsageChart logs={logs} />

          {/* Storico interventi (laboratorio + domicilio) */}
          <Card className="border-border/70 rounded-lg overflow-hidden" data-testid="interventions-history-card">
            <div className="px-4 py-3 border-b border-border/60 bg-secondary/40 flex items-center gap-2">
              <Wrench size={18} weight="duotone" className="text-primary" />
              <h2 className="font-display font-bold tracking-tight">Storico interventi <span className="font-mono text-xs text-muted-foreground ml-1">({interventions.length})</span></h2>
            </div>
            {interventions.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground/70 font-mono">— Nessun intervento registrato —</div>
            ) : (
              <>
                <div className="divide-y divide-border/50">
                  {intPageItems.map((it) => {
                  const isHome = it.type === "home";
                  const closed = it.status === "closed";
                  return (
                    <Link
                      to={`/interventi/${it.id}`}
                      key={it.id}
                      className="block p-4 hover:bg-secondary/40"
                      style={{ transition: "background-color 0.15s ease" }}
                      data-testid={`intervention-row-${it.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`h-8 w-8 shrink-0 rounded-full grid place-items-center ${isHome ? "bg-sky-500/10 text-sky-700 dark:text-sky-400" : "bg-primary/10 text-primary"}`}>
                          {isHome ? <HouseSimple size={14} weight="duotone" /> : <Wrench size={14} weight="duotone" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-mono text-xs text-muted-foreground">{it.code}</div>
                            <Badge className={`font-mono text-[9px] uppercase border hover:bg-transparent ${isHome ? "bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/40" : "bg-primary/10 text-primary border-primary/30"}`}>
                              {isHome ? "Domicilio" : "Laboratorio"}
                            </Badge>
                            <Badge className={`font-mono text-[9px] uppercase border hover:bg-transparent ${closed ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/40" : "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/40"}`}>
                              {closed ? "Chiuso" : "Aperto"}
                            </Badge>
                            <span className="font-mono text-[10px] text-muted-foreground">{formatDateIT(it.created_at?.slice(0, 10))}</span>
                            {it.price_final != null && it.price_final > 0 && (
                              <Badge className="bg-secondary text-foreground border-border font-mono text-[9px] hover:bg-secondary">{eur(it.price_final)}</Badge>
                            )}
                          </div>
                          <div className="text-sm mt-1 truncate">
                            <span className="font-medium">{it.product_type}</span>
                            {it.product_brand && <span className="text-muted-foreground"> · {it.product_brand}</span>}
                            {it.product_model && <span className="text-muted-foreground"> {it.product_model}</span>}
                          </div>
                          <div className="text-xs text-muted-foreground truncate mt-0.5">{it.work_description}</div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
                {intTotalPages > 1 && (
                  <div className="px-4 py-2 border-t border-border/60 bg-secondary/20 flex items-center justify-between text-xs font-mono">
                    <span className="text-muted-foreground">Pagina {intCurPage}/{intTotalPages} · {intStart + 1}-{Math.min(intStart + PAGE_SIZE, interventions.length)} di {interventions.length}</span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" disabled={intCurPage === 1} onClick={() => setIntPage((p) => Math.max(1, p - 1))} data-testid="int-prev-page" className="h-7 px-2 rounded-full">‹ Prec</Button>
                      <Button size="sm" variant="outline" disabled={intCurPage === intTotalPages} onClick={() => setIntPage((p) => Math.min(intTotalPages, p + 1))} data-testid="int-next-page" className="h-7 px-2 rounded-full">Succ ›</Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      </div>

      <AssistanceLogDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        customerId={id}
        plan={plan}
        remote={remote}
        onSaved={() => { setDialogOpen(false); load(); }}
      />
    </div>
  );
}

function AssistanceLogDialog({ open, onOpenChange, customerId, plan, remote, onSaved }) {
  const [form, setForm] = useState({ date: todayIso(), note: "", minutes_used: "", price: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ date: todayIso(), note: "", minutes_used: "", price: "" });
  }, [open]);

  const minutes = parseInt(form.minutes_used, 10) || 0;

  // Live scaling preview (client-side, matches backend algorithm)
  const planRem = plan?.monthly_minutes_remaining ?? 0;
  const remoteAvail = remote?.available ?? 0;
  const fromPlan = Math.min(planRem, minutes);
  const afterPlan = Math.max(0, minutes - fromPlan);
  const fromRemote = Math.min(remoteAvail, afterPlan);
  const uncovered = Math.max(0, afterPlan - fromRemote);
  const totalCoverage = planRem + remoteAvail;
  const blocked = minutes > 0 && uncovered > 0;

  const submit = async () => {
    if (!form.note.trim()) return toast.error("Descrivi cosa hai fatto");
    if (minutes <= 0) return toast.error("Indica i minuti da scalare (>0)");
    if (blocked) return toast.error(`Copertura insufficiente: disponibili solo ${totalCoverage} min`);
    setSaving(true);
    try {
      await api.post(`/customers/${customerId}/assistance-logs`, {
        date: form.date || todayIso(),
        note: form.note.trim(),
        minutes_used: minutes,
        price: form.price === "" ? null : parseFloat(form.price),
      });
      toast.success("Assistenza registrata");
      emitServicesChanged(customerId);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="assistance-log-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Registra assistenza</DialogTitle>
          <DialogDescription>
            I minuti vengono scalati automaticamente: prima dal <b>Piano Gestito</b>, poi dal <b>Saldo Remoto</b>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Data</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="log-date-input" className="mt-1.5" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Minuti totali</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={form.minutes_used}
                onChange={(e) => setForm({ ...form, minutes_used: e.target.value })}
                placeholder="Es. 20"
                data-testid="log-minutes-input"
                className="mt-1.5 font-mono"
              />
            </div>
          </div>

          {/* Live preview */}
          {minutes > 0 && (
            <div className={`rounded-md border p-3 space-y-1 text-xs font-mono ${blocked ? "border-rose-500/40 bg-rose-500/5" : "border-border/60 bg-secondary/40"}`} data-testid="scaling-preview">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Ripartizione automatica</div>
              <div className="flex justify-between">
                <span>Dal Piano Gestito</span>
                <span className="font-bold">{fromPlan} min <span className="text-muted-foreground font-normal">(res. {planRem - fromPlan}/{planRem})</span></span>
              </div>
              <div className="flex justify-between">
                <span>Dal Saldo Remoto</span>
                <span className="font-bold">{fromRemote} min <span className="text-muted-foreground font-normal">(res. {remoteAvail - fromRemote}/{remoteAvail})</span></span>
              </div>
              {uncovered > 0 && (
                <div className="flex justify-between text-rose-700 dark:text-rose-400 pt-1 border-t border-rose-500/30 mt-1">
                  <span className="flex items-center gap-1"><Warning size={12} weight="fill" /> Non coperti</span>
                  <span className="font-bold">{uncovered} min</span>
                </div>
              )}
              {blocked && (
                <div className="text-[10px] text-rose-700 dark:text-rose-400 mt-1 pt-1 border-t border-rose-500/30">
                  Impossibile registrare: vendi un pacchetto REMOTE o attiva un piano per aumentare la copertura.
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Cosa hai fatto</Label>
            <Textarea rows={4} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Es. Aggiornamento antivirus + risoluzione problema stampante di rete" data-testid="log-note-input" className="mt-1.5" />
          </div>

          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Prezzo one-off (opzionale)</Label>
            <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5 font-mono" data-testid="log-price-input" placeholder="Solo se non incluso in un piano" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving || blocked || minutes <= 0 || !form.note.trim()} className="rounded-full gap-1" data-testid="save-log-btn">
            <Plus size={14} /> {saving ? "Salvo…" : "Registra"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
